import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import RTL from './components/RTL';
import Header from './components/Header';
import BackButton from './components/BackButton';
import AdminLayout from './components/AdminLayout';
import AdminSidebar from './components/AdminSidebar';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import OpenBus from './pages/OpenBus';
import ValidatePassengers from './pages/ValidatePassengers';
import { AuthProvider } from './auth/AuthProvider';
import { useAuth } from './auth/useAuth';
import { ProjectProvider } from './contexts/ProjectContext';
import ManagerDashboard from './pages/ManagerDashboard';
import UserList from './pages/UserList';
import UserEdit from './pages/UserEdit';
import TouristList from './pages/TouristList';
import PassengerDashboard from './pages/PassengerDashboard'; 
import BusList from './pages/BusList'; 
import BusEdit from './pages/BusEdit'; 
import FlightSeatingList from './pages/FlightSeatingList';
import AircraftReplacementManagement from './pages/AircraftReplacementManagement';
import Settings from './pages/Settings';
import BusListUser from './pages/BusListUser';
import CashBoxManagement from './pages/CashBoxManagement';
import TransactionsList from './pages/TransactionsList';
import ProjectList from './pages/ProjectList';
import ResetPasswordRequest from './pages/ResetPasswordRequest';
import ResetPasswordForm from './pages/ResetPasswordForm';
import TaxScannerPage from './pages/TaxScannerPage';

const theme = createTheme({
  direction: 'rtl',
});

const PrivateRoute = ({ children }) => {
  const { user, isAuthChecked } = useAuth();
  
  if (!isAuthChecked) {
    return <div>טוען...</div>;
  }
  
  if (!user) {
    console.log('User not found in PrivateRoute, redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, isAuthChecked } = useAuth();
  
  if (!isAuthChecked) {
    return <div>טוען...</div>;
  }
  
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const ManagerRoute = ({ children }) => {
  const { user, isAuthChecked } = useAuth();
  
  if (!isAuthChecked) {
    return <div>טוען...</div>;
  }
  
  if (!user || user.role !== 'manager') {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const NonTaxRoute = ({ children }) => {
  const { user, isAuthChecked } = useAuth();
  
  if (!isAuthChecked) {
    return <div>טוען...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // משתמש tax יכול לגשת רק לדף הבית
  if (user.role === 'tax') {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppContent() {
  const { isAuthChecked, user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => {
    setSidebarOpen((prevOpen) => !prevOpen);
  };

  if (!isAuthChecked) {
    return <div>טוען...</div>;
  }

  const isManager = user && user.role === 'manager';
  const isTaxUser = user && user.role === 'tax';

  return (
    <ThemeProvider theme={theme}>
      <RTL>
        <CssBaseline />
        <Router>
        {!isTaxUser && <Header toggleSidebar={toggleSidebar} />}
        {!isTaxUser && <BackButton />}
        {!isTaxUser && <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />}
          <Routes>
            <Route 
              path="/" 
              element={
                <PrivateRoute>
                  {user?.role === 'tax' ? <TaxScannerPage /> : <Dashboard />}
                </PrivateRoute>
              } 
            />
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } 
            />
            <Route 
              path="/register" 
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              } 
            />
            <Route 
              path="/reset-password-request" 
              element={
                <PublicRoute>
                  <ResetPasswordRequest />
                </PublicRoute>
              } 
            />
            <Route 
              path="/reset-password/:token" 
              element={
                <PublicRoute>
                  <ResetPasswordForm />
                </PublicRoute>
              } 
            />
            <Route 
              path="/open-bus" 
              element={
                <NonTaxRoute>
                  <OpenBus />
                </NonTaxRoute>
              } 
            />
            <Route 
              path="/open-bus/:id" 
              element={
                <NonTaxRoute>
                  <OpenBus />
                </NonTaxRoute>
              } 
            />
            <Route 
              path="/my-buses" 
              element={
                <NonTaxRoute>
                  <BusListUser />
                </NonTaxRoute>
              } 
            />
            <Route 
              path="/validate-passengers/:busId" 
              element={
                <NonTaxRoute>
                  <ValidatePassengers />
                </NonTaxRoute>
              } 
            />
            <Route 
              path="/manager-dashboard" 
              element={
                <ManagerRoute>
                  <AdminLayout>
                    <ManagerDashboard />
                  </AdminLayout>
                </ManagerRoute>
              } 
            />
            <Route 
              path="/projects" 
              element={
                <NonTaxRoute>
                  <AdminLayout>
                    <ProjectList />
                  </AdminLayout>
                </NonTaxRoute>
              } 
            />
            <Route 
              path="/admin/users" 
              element={
                <ManagerRoute>
                  <AdminLayout>
                    <UserList />
                  </AdminLayout>
                </ManagerRoute>
              } 
            />
            <Route 
              path="/admin/users/edit/:id" 
              element={
                <ManagerRoute>
                  <AdminLayout>
                    <UserEdit />
                  </AdminLayout>
                </ManagerRoute>
              } 
            />
            <Route 
              path="/admin/tourists" 
              element={
                <ManagerRoute>
                  <AdminLayout>
                    <TouristList />
                  </AdminLayout>
                </ManagerRoute>
              } 
            />
            <Route 
              path="/admin/passenger-dashboard" 
              element={
                <ManagerRoute>
                  <AdminLayout>
                    <PassengerDashboard />
                  </AdminLayout>
                </ManagerRoute>
              } 
            />
            <Route 
              path="/admin/buses" 
              element={
                <ManagerRoute>
                  <AdminLayout>
                    <BusList />
                  </AdminLayout>
                </ManagerRoute>
              } 
            />
            <Route 
              path="/edit-bus/:id" 
              element={
                <NonTaxRoute>
                  <AdminLayout>
                    <BusEdit />
                  </AdminLayout>
                </NonTaxRoute>
              } 
            />
            <Route 
              path="/admin/flight-seating" 
              element={
                <ManagerRoute>
                  <AdminLayout>
                    <FlightSeatingList />
                  </AdminLayout>
                </ManagerRoute>
              } 
            />
            <Route 
              path="/admin/aircraft-replacement" 
              element={
                <ManagerRoute>
                  <AdminLayout>
                    <AircraftReplacementManagement />
                  </AdminLayout>
                </ManagerRoute>
              } 
            />
            <Route 
              path="/admin/settings" 
              element={
                <ManagerRoute>
                  <AdminLayout>
                    <Settings />
                  </AdminLayout>
                </ManagerRoute>
              } 
            />
            <Route 
              path="/admin/cash-boxes" 
              element={
                <ManagerRoute>
                  <AdminLayout>
                    <CashBoxManagement />
                  </AdminLayout>
                </ManagerRoute>
              } 
            />
            <Route 
              path="/admin/transactions" 
              element={
                <ManagerRoute>
                  <AdminLayout>
                    <TransactionsList />
                  </AdminLayout>
                </ManagerRoute>
              } 
            />
          </Routes>
        </Router>
      </RTL>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <ProjectProvider>
        <AppContent />
      </ProjectProvider>
    </AuthProvider>
  );
}

export default App;